import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityPicker, itemRender } from "@/components/app/EntityPicker";
import { FieldGrid } from "@/components/assets/FieldGrid";
import { api, errMsg } from "@/lib/api";
import { useLookups } from "@/hooks/useLookups";
import { useSerialFlags } from "@/components/logistics/SerialCapture";

const STATUSES = ["operational", "under_repair", "down", "decommissioned"];
const IDS = [["serial_no", "Serial no."], ["mfr_serial_no", "Manufacturer serial no."], ["model_no", "Model no."], ["capacity_spec", "Capacity / spec summary"], ["quantity", "Quantity", "number"]];
const DATES = [["installation_date", "Installation", "date"], ["commissioning_date", "Commissioning", "date"], ["handover_date", "Handover", "date"],
  ["warranty_start", "Warranty start", "date"], ["warranty_end", "Warranty end", "date"], ["amc_start", "AMC start", "date"], ["amc_end", "AMC end", "date"]];
const BLANK = { asset_code: "", unit_name: "", asset_type: "", item_code: "", item_name: "", serial_no: "", mfr_serial_no: "", model_no: "", capacity_spec: "", quantity: 1,
  installation_date: "", commissioning_date: "", handover_date: "", warranty_start: "", warranty_end: "", amc_start: "", amc_end: "",
  unit_status: "operational", service_owner: "", notes: "" };

function SerialHint({ code, serial }) {
  const tracked = useSerialFlags(code ? [code] : []);
  if (!code || !tracked.has(code)) return null;
  return <p className={`sm:col-span-3 text-xs ${serial ? "text-slate-500" : "text-amber-700"}`} data-testid="au-serial-tracked-hint">Item {code} is serial-tracked - a serial number is required.</p>;
}


export function AssetUnitForm({ open, onOpenChange, group, unit, onSaved, billingToggle = false, lmo = null }) {
  const [billing, setBilling] = useState(true);
  const { opts } = useLookups();
  const types = [...new Set([...opts("asset_type"), ...(unit?.asset_type ? [unit.asset_type] : [])])];
  const [f, setF] = useState(BLANK);
  const [part, setPart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("direct");

  useEffect(() => {
    if (!open) return;
    setBilling(true);
    setMode(lmo ? "preloaded" : "direct");
    setF(unit ? Object.fromEntries(Object.keys(BLANK).map((k) => [k, unit[k] ?? BLANK[k]])) : BLANK);
    setPart(unit?.item_code && unit.item_linked !== false ? { ...unit.item_snapshot, item_code: unit.item_code } : null);
  }, [open, unit, lmo]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, group_id: group.id, item_code: part?.item_code || f.item_code || null, quantity: Number(f.quantity) || 1, billing_prompt: billingToggle ? billing : true,
        addition_mode: mode, source_order_id: mode === "preloaded" ? lmo?.id : "" };
      const r = unit ? await api.put(`/assets/units/${unit.id}`, body) : await api.post("/assets/units", body);
      toast.success(unit ? "Asset unit updated" : `${r.data.asset_code} created`);
      onOpenChange(false);
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const inherit = { handover_date: group?.handover_date, warranty_start: group?.warranty_start, warranty_end: group?.warranty_end };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="asset-unit-form">
        <DialogHeader><DialogTitle>{unit ? `Edit ${unit.asset_code}` : `New asset unit in ${group?.asset_group_code}`}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">An asset unit is a serviceable item that was installed and handed over, not a BOM row. One serial number = one unit.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FieldGrid prefix="au" f={f} setF={setF} defs={[["unit_name", "Unit name (blank = item name)"], ...(unit ? [] : [["asset_code", "Asset code (blank = auto AU-…)"]]), ["asset_type", "Asset type", "select", types]]} />
          <div><Label>Item (Item Master)</Label><EntityPicker url="/items?status=ACTIVE,BLOCKED" value={part} onChange={setPart} render={itemRender} placeholder="Search Item Master" testid="au-part-picker" /></div>
          {!part && <FieldGrid prefix="au" f={f} setF={setF} defs={[["item_code", "Or type item code (historical)"], ["item_name", "Item name"]]} />}
          <FieldGrid prefix="au" f={f} setF={setF} defs={IDS} />
          <SerialHint code={part?.item_code || f.item_code} serial={f.serial_no} />
        </div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-2">Dates (blank handover/warranty dates are taken from the group)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3"><FieldGrid prefix="au" f={f} setF={setF} defs={DATES} placeholders={inherit} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"><FieldGrid prefix="au" f={f} setF={setF} defs={[["unit_status", "Serviceability status", "select", STATUSES], ["service_owner", "Service owner (blank = group)"]]} /></div>
        <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="au-notes-input" /></div>
        <DialogFooter className="items-center gap-3">
          {billingToggle && !unit && <label className="flex items-center gap-2 text-xs mr-auto"><input type="checkbox" checked={billing} onChange={(e) => setBilling(e.target.checked)} data-testid="au-billing-prompt-check" />Flag a billing change on the LMO's matching billing period</label>}
          {!unit && <select className="h-9 rounded border border-slate-200 bg-white px-2 text-xs" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="au-addition-mode-select">
            {lmo && <option value="preloaded">Preloaded - becomes Active when its challan is delivered</option>}
            <option value="historical">Historical direct upload - Active now (already on site)</option>
            {!lmo && <option value="direct">Direct addition - Active now</option>}
          </select>}
          <Button onClick={save} disabled={busy} data-testid="au-save-btn">{busy ? "Saving…" : "Save asset unit"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
